/**
 * ============================================================================
 * ReferralForm / ReferralFormNew — Model (form values + RHF resolver)
 * ============================================================================
 * Explicit resolver replaces manual errors state. No Yup.
 * `strictFullName` enables ReferralFormNew special-char rules.
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";
import {
  errorAccordingToFieldName,
  regexForInputs,
} from "@/components/organisms/faq-hub/HomeNewFaq/HomeConnectModalFormFAQ/utils";

export const NATIONAL_CODE = "+91";

export const EMAIL_REGEX = /^[\w.%+-]+@[\w.-]+\.[a-zA-Z]{2,}$/;

export interface ReferralProgramOption {
  index: number;
  ProgramID: number;
  ProgramName: string;
  ProgramType?: string;
  Specialization: string;
  Custom10PgType?: string | null;
  ProgramVertical?: string;
  Program?: string;
}

export interface ReferralFormValues {
  fullName: string;
  email: string;
  countryCode: string;
  mobile: string;
  program: {
    ProgramID: number;
    ProgramName: string;
    Specialization: string;
    index: number;
  };
  counsellorUsername: string;
  verificationEmailOrMobile: string;
  otp: string[];
}

export interface ReferralReferrerData {
  AmityEmail: string;
  EnrollmentNo: string;
  Name: string;
  PersonalEmail: string;
  Phone: string;
  ProgramName: string;
  AMSID: string;
  Status?: string;
}

export const EMPTY_PROGRAM: ReferralFormValues["program"] = {
  ProgramID: 0,
  ProgramName: "",
  Specialization: "",
  index: 0,
};

export const EMPTY_REFERRER_DATA: ReferralReferrerData = {
  AmityEmail: "",
  EnrollmentNo: "",
  Name: "",
  PersonalEmail: "",
  Phone: "",
  ProgramName: "",
  AMSID: "",
};

export function validateReferralFullName(
  value: string,
  strictFullName: boolean,
): string {
  if (!value.trim()) return "Full Name is required.";
  if (strictFullName) {
    if (value.trim().length < 3) {
      return "Full Name must be at least 3 characters.";
    }
    if (/\d/.test(value)) return "Full Name should not contain numbers.";
    if (!/^[A-Za-z\s]+$/.test(value)) {
      return "Full Name should not contain special characters.";
    }
    return "";
  }
  if (value.length < 3) return "Full Name must be at least 3 characters.";
  if (/\d/.test(value)) return "Full Name should not contain numbers.";
  return "";
}

export function validateReferralEmail(value: string): string {
  if (!value.trim()) return "Email is required.";
  if (!EMAIL_REGEX.test(value)) return "Enter a valid email address.";
  return "";
}

export function validateReferralMobile(
  value: string,
  countryCode: string = NATIONAL_CODE,
): string {
  let regexToUse: RegExp = regexForInputs.phoneNumber;
  if (countryCode !== NATIONAL_CODE) {
    regexToUse = /^[1-9][0-9]{6,14}$/;
  }
  if (!regexToUse.test(value)) {
    return errorAccordingToFieldName.phoneNumber;
  }
  return "";
}

export function validateVerificationEmailOrMobile(value: string): string {
  if (!value.trim()) return "Verification email or mobile is required.";
  if (/^[a-zA-Z]/.test(value)) {
    return !EMAIL_REGEX.test(value) ? "Enter a valid email address." : "";
  }
  if (/^\d/.test(value)) {
    return !/^\d{10}$/.test(value)
      ? "Enter a valid 10-digit mobile number."
      : "";
  }
  return "Invalid input. Please enter a valid email or mobile number.";
}

export function validateCounsellorUsername(value: string): string {
  if (value && !/^[a-zA-Z0-9]+$/.test(value)) {
    return "Username must not include special characters or spaces";
  }
  return "";
}

export interface CreateReferralFormResolverOptions {
  strictFullName: boolean;
}

export function createReferralFormResolver(
  options: CreateReferralFormResolverOptions,
): Resolver<ReferralFormValues> {
  const { strictFullName } = options;

  return (values) => {
    const errors: FieldErrors<ReferralFormValues> = {};

    const fullNameErr = validateReferralFullName(
      values.fullName,
      strictFullName,
    );
    if (fullNameErr) {
      errors.fullName = { type: "validate", message: fullNameErr };
    }

    const emailErr = validateReferralEmail(values.email);
    if (emailErr) {
      errors.email = { type: "validate", message: emailErr };
    }

    if (
      !values.countryCode ||
      !regexForInputs.countryCode?.test(values.countryCode)
    ) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const mobileErr = validateReferralMobile(
      values.mobile,
      values.countryCode,
    );
    if (mobileErr) {
      errors.mobile = { type: "validate", message: mobileErr };
    }

    if (!values.program?.ProgramID) {
      errors.program = {
        type: "validate",
        message: "Please select a course.",
      };
    }

    const counsellorErr = validateCounsellorUsername(
      values.counsellorUsername || "",
    );
    if (counsellorErr) {
      errors.counsellorUsername = {
        type: "validate",
        message: counsellorErr,
      };
    }

    const verifyErr = validateVerificationEmailOrMobile(
      values.verificationEmailOrMobile,
    );
    if (verifyErr) {
      errors.verificationEmailOrMobile = {
        type: "validate",
        message: verifyErr,
      };
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}
